import { Autocomplete, Button, Checkbox, CircularProgress, FormControlLabel, Grid, Paper, TextField } from '@mui/material'
import DateRangePicker from 'components/DateRangePicker/DateRangePicker'
import { useSession } from 'components/Providers'
import RequiredLabel from 'components/RequiredLabel'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'

const ACADEMIC_YEAR_PARAM = 'academic_year_provider_id'

const YEARS_BACK = 3
const YEARS_FORWARD = 1

const reportingWindow = () => {
    const today = dayjs()
    const startYear = today.month() >= 6 ? today.year() : today.year() - 1

    return {
        startDate: dayjs(new Date(startYear - YEARS_BACK, 6, 1)).format('YYYY-MM-DD'),
        endDate: dayjs(new Date(startYear + YEARS_FORWARD + 1, 5, 30)).format('YYYY-MM-DD'),
    }
}

export default function ReportParams({ paramsData, selectedValues, tableDataLoading, onSelect, onGenerate }) {
  const { t } = useTranslation()
  const { academicYear } = useSession()
  const hasYearParam = paramsData.some(param => param.param === ACADEMIC_YEAR_PARAM)
  const dateBounds = hasYearParam ? reportingWindow() : academicYear

  const allRequiredSelected = paramsData.every((param) => {
    if (!param.required) return true

    const value = selectedValues[param.param]

    switch (param.ui?.type) {
      case 'checkbox':
        return value === true
      case 'daterange':
        return Array.isArray(value) && value[0] && value[1]
      default:
        return Boolean(value)
    }
  })

  const renderField = (param) => {
    switch (param.ui?.type) {
      case 'daterange': {
        const rawValue = selectedValues[param.param]
        const value = Array.isArray(rawValue) ? rawValue : [null, null]

        return (
          <DateRangePicker
            key={param.id} 
            label={param.required ? <RequiredLabel>{param.name}</RequiredLabel> : param.name}
            startDate={value[0]} 
            endDate={value[1]}
            minDate={dateBounds?.startDate ?? '2000-01-01'}
            maxDate={dateBounds?.endDate ?? dayjs().endOf('year').format('YYYY-MM-DD')}
            setDateRange={(range) => onSelect(param, range, 'daterange')}
            showQuickSelect={true}
          />
        )
      }

      case 'checkbox':
        return (
          <Paper
            variant="outlined"
            sx={{
              width: '100%',
              px: 1.25,
              py: 0.5,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              borderRadius: 1,
            }}
          >
            <FormControlLabel
              sx={{ m: 0, width: '100%' }}
              control={
          <Checkbox
            size="small"
            checked={!!selectedValues[param.param]}
            onChange={(e) => onSelect(param, e.target.checked, 'checkbox')}
          />
              }
              label={param.required ? <RequiredLabel>{param.name}</RequiredLabel> : param.name}
            />
          </Paper>
        )

      default:
        return (
          <Autocomplete
            value={selectedValues[param.param] || null}
            options={param.options || []}
            onChange={(e, opt, reason) => onSelect(param, opt, reason)}
            getOptionLabel={(o) => o?.name || ''}
            isOptionEqualToValue={(o, v) => o?.id === v?.id}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label={param.required ? <RequiredLabel>{param.name}</RequiredLabel> : param.name}
              />
            )}
          />
        )
    }
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Grid container spacing={1}>
        {paramsData.map((param) => (
          <Grid item xs={12} sm={6} md={param.ui.width || 3} key={param.id}>
            {renderField(param)}
          </Grid>
        ))}

        <Grid item xs="auto">
          <Button
            variant="contained"
            onClick={onGenerate}
            disabled={!allRequiredSelected || tableDataLoading}
            startIcon={tableDataLoading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {tableDataLoading ? t('Generating...') : t('Generate')}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  )
}
